// Multi-entity — what a report actually covered.
//
// Two states, and the distinction is the point:
//
//   NARROWED   the report ran over some of the tenant's companies. Shown inline
//              next to the row count, because a total over two of four
//              companies is a wrong number unless the reader knows.
//   NO ACCESS  the viewer has no entity grant at all, so the report is empty
//              for a reason that has nothing to do with the filters. Without
//              this, "No rows" sends someone to look for missing payroll data.
//
// The label comes from the server so the screen, the CSV and the XLSX cannot
// disagree about it.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import type { ReportScopeNote } from '@/services/reportEngineService';

interface Props {
  scope?: ReportScopeNote | null;
}

/** Inline companies-covered line. Renders nothing when the viewer is unrestricted. */
export const ReportScopeNotice: React.FC<Props> = ({ scope }) => {
  const { t } = useTranslation();
  if (!scope?.restricted) return null;
  const partial = scope.companies_total > 0 && scope.companies_included < scope.companies_total;
  return (
    <span
      className={`ml-2 inline-flex items-center gap-1 ${partial ? 'text-warning' : 'text-muted-foreground'}`}
      title={t('reportScope.tooltip', 'Reports cover only the companies you have been granted access to.')}
    >
      <Building2 className="h-3.5 w-3.5" aria-hidden />
      {scope.label}
    </span>
  );
};

/**
 * Explains an empty result that is empty because of access, not filters.
 * Renders nothing unless the viewer's reach is zero companies.
 */
export const ReportScopeEmptyReason: React.FC<Props> = ({ scope }) => {
  const { t } = useTranslation();
  if (!scope?.restricted || scope.companies_included > 0) return null;
  return (
    <p className="mt-2 text-sm text-warning">
      {t(
        'reportScope.noCompanies',
        'You have not been granted access to any company, so this report has nothing to draw on. Ask an administrator for entity access.',
      )}
    </p>
  );
};

export default ReportScopeNotice;
